import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-slate-200 rounded-lg"
        aria-expanded={isOpen}
      >
        <span className="text-slate-900 font-medium pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-slate-600 leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const { siteContent } = useSiteContent();
  const section = siteContent?.faq || {};
  const [openIndex, setOpenIndex] = useState(0);
  
  if (section.visible === false) return null;

  const title = section.title || 'Frequently Asked Questions';
  const items = section.items || [];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-slate-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900">
            {title}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-100"
        >
          {items.map((item, index) => {
            const question = item.question || '';
            const answer = item.answer || '';
            
            return (
              <FAQItem
                key={`faq-${index}`}
                question={question}
                answer={answer}
                isOpen={openIndex === index}
                onClick={() => handleToggle(index)}
              />
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
